import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { FamilySystemRecaptureResult } from "@/lib/calculationEngine";
import { createFormatter, type Jurisdiction } from "@/lib/formatters";

interface CostComparisonProps {
  recapture: FamilySystemRecaptureResult;
  jurisdiction: Jurisdiction;
  isInterestOnly?: boolean;
}

export function CostComparison({ recapture, jurisdiction, isInterestOnly }: CostComparisonProps) {
  const { fmt } = createFormatter(jurisdiction);
  const [expanded, setExpanded] = useState(false);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">Cost Comparison</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-4">
          <div className="text-center p-4 rounded-md bg-secondary">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Paid to Life Company</p>
            <p className="text-[10px] text-muted-foreground">(Your Co-Ownership Pool)</p>
            <p className="text-lg sm:text-xl font-semibold tabular-nums mt-1">{fmt(recapture.actualPolicyLoanInterestCost)}</p>
          </div>
          <div className="text-center p-4 rounded-md bg-secondary">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Intended Total Interest</p>
            <p className="text-[10px] text-muted-foreground">(Intended Loan)</p>
            <p className="text-lg sm:text-xl font-semibold tabular-nums mt-1">{fmt(recapture.intendedTotalInterest)}</p>
          </div>
          <div className="text-center p-4 rounded-md bg-positive/10">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Additional Financing Margin</p>
            <p className="text-lg sm:text-xl font-semibold tabular-nums text-positive mt-1">{fmt(recapture.additionalIntendedFinancingMargin)}</p>
          </div>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed">
          {recapture.explanationText}
        </p>

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 mt-3 text-xs font-medium text-accent hover:text-accent/80 transition-colors"
        >
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {expanded ? 'Hide' : 'Show'} educational note
        </button>

        {expanded && (
          <div className="mt-3 p-4 rounded-md bg-secondary text-sm text-muted-foreground leading-relaxed">
            <p className="font-medium text-foreground mb-1">The "Stealing the Peas" Principle</p>
            <p>
              Nelson Nash, in <em>Becoming Your Own Banker</em>, illustrated the concept of recapturing 
              interest costs using the analogy of "stealing the peas." When you borrow from a traditional 
              lender, the interest leaves your family system permanently. By structuring loans through your 
              own banking system — whole life insurance policies — the interest payments circulate back 
              into your system, building cash value and death benefit. The additional financing margin 
              above the actual policy loan cost represents the "peas" you can redirect into your family 
              system premiums, keeping financial energy within your control.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
